import {
    CorporationJoinMode,
    LocationJoinMode,
    NotificationType,
} from '@kcftech/prometheustypescriptapiclient';
import { CreateButton } from 'components/button/CreateButton';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { User } from 'components/dropdown/UserDropdown';
import { TablePageLayout } from 'components/page/styled';
import { usePermissions } from 'contexts/PermissionsContext';
import { useUser } from 'contexts/UserContext';
import {
    NotificationSubscriptionsFilter,
    usePagedNotificationSubscriptionsDataSource,
} from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import { CreateNotificationSubscriptionModal } from 'pages/Settings/NotificationSubscriptions/CreateNotificationSubscriptionModal';
import { NotificationSubscriptionsFilterPane } from 'pages/Settings/NotificationSubscriptions/NotificationSubscriptionsFilterPane';
import {
    SearchCreateContainer,
    SelectedUserDropdown,
    UserSelectionHeading,
} from 'pages/Settings/NotificationSubscriptions/styled';
import { UserNotificationSubscriptionsTable } from 'pages/Settings/NotificationSubscriptions/UserNotificationSubscriptionsTable';
import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import { queryClient } from 'utils/query';
import { ContextualPageProps } from 'utils/types';

export const indicatorAlertsUserViewTableQueryKey = 'asset-health-subscriptions-table';
export const hardwareUserViewTableQueryKey = 'hardware-subscriptions-table';

export function UserViewTab({ corporation, location }: ContextualPageProps) {
    const { user } = useUser();

    const [nameSearch, setNameSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState<User | undefined>(
        user ? { ...user, ApplicationRoleCode: user.ApplicationRole } : undefined
    );
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [typeFilters, setTypeFilters] = useState<NotificationType[]>([]);

    const defaultFilter = useMemo<NotificationSubscriptionsFilter>(
        () => ({
            corporationId: corporation?.Id,
            locationId: location?.Id,
            userId: selectedUser?.Id,
            nameSearch,
        }),
        [corporation?.Id, location?.Id, nameSearch, selectedUser?.Id]
    );

    const isSmallScreen = useIsViewportAtMost(MediaSize.small);

    const dataSource = usePagedNotificationSubscriptionsDataSource({
        name: indicatorAlertsUserViewTableQueryKey,
        defaultFilter,
    });

    const { setFilter } = dataSource;
    useEffect(() => {
        setFilter((current) => ({
            ...current,
            nameSearch,
            notificationTypes: typeFilters,
            userId: selectedUser?.Id,
            locationId: location?.Id,
            corporationId: corporation?.Id,
        }));
    }, [
        setFilter,
        nameSearch,
        typeFilters,
        selectedUser?.Id,
        location?.Id,
        corporation?.Id,
    ]);

    return (
        <TablePageLayout>
            <div className="flex-row flex-wrap mb-20">
                <SearchCreateContainer>
                    <div className="flex-row align-center">
                        <div className="pr-10">
                            <NotificationSubscriptionsFilterPane
                                filters={typeFilters}
                                onChange={setTypeFilters}
                            />
                        </div>
                        <DebouncedSearchInput
                            placeholder="Search"
                            value={nameSearch}
                            onChange={(text) => setNameSearch(text)}
                        />
                        {!isSmallScreen && (
                            <UserSelection
                                corporation={corporation}
                                location={location}
                                selectedUser={selectedUser}
                                setSelectedUser={setSelectedUser}
                            />
                        )}
                    </div>
                    <CreateButton
                        title="Create Notification Subscription"
                        ariaLabel="Create Notification Subscription"
                        onClick={() => setIsCreateModalVisible(true)}
                    />
                </SearchCreateContainer>
                {isSmallScreen && (
                    <UserSelection
                        corporation={corporation}
                        location={location}
                        selectedUser={selectedUser}
                        setSelectedUser={setSelectedUser}
                    />
                )}
            </div>
            <UserNotificationSubscriptionsTable
                dataSource={dataSource}
                location={location}
                corporation={corporation}
            />
            {isCreateModalVisible && (
                <CreateNotificationSubscriptionModal
                    location={location}
                    corporation={corporation}
                    user={selectedUser}
                    handleClose={() => setIsCreateModalVisible(false)}
                    onCreate={() => {
                        queryClient.invalidateQueries({
                            queryKey: [indicatorAlertsUserViewTableQueryKey],
                        });
                        queryClient.invalidateQueries({
                            queryKey: [hardwareUserViewTableQueryKey],
                        });
                    }}
                    defaultNotificationType={NotificationType.Indicator}
                />
            )}
        </TablePageLayout>
    );
}

type UserSelectionProps = ContextualPageProps &
    Readonly<{
        selectedUser: User | undefined;
        setSelectedUser: React.Dispatch<React.SetStateAction<User | undefined>>;
    }>;

function UserSelection({
    corporation,
    location,
    selectedUser,
    setSelectedUser,
}: UserSelectionProps) {
    const { canControlOtherUsersNotificationSubscriptions } = usePermissions();

    return (
        <UserSelectionHeading>
            Subscriptions for{' '}
            {selectedUser &&
                (canControlOtherUsersNotificationSubscriptions ? (
                    <SelectedUserDropdown
                        value={selectedUser}
                        onChange={(newUser) => setSelectedUser(newUser)}
                        corporationId={corporation?.Id}
                        locationId={location?.Id}
                        corporationJoinOverride={
                            corporation ? CorporationJoinMode.CanViewWithin : undefined
                        }
                        locationJoinOverride={
                            location ? LocationJoinMode.CanViewAt : undefined
                        }
                        onlyActiveUsers
                    />
                ) : (
                    `${selectedUser.FirstName} ${selectedUser.LastName}`
                ))}
        </UserSelectionHeading>
    );
}
